import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAuthInfo } from "../allauth/auth/hooks";
import ApiClient, { HttpResponse } from "../config/ApiClient";
import { useRoleLabels } from "../hooks/useRoleLabels";
import type {
    AssignmentInvitation,
    AthletePaymentAssignment,
} from "../object-actions/types/types";
import {
    assignmentHasCoachingSupport,
    clearStoredReturnParams,
    computeOnboardingSteps,
    consumeReturnTarget,
    getParticipationRole,
    getReturnSearch,
    loadStoredReturnSearch,
    OnboardingState,
    storeReturnParams,
    withReturn as withReturnUtil,
} from "../utils/onboarding";
import { useAppContext } from "./AppContext";

interface OnboardingContextType extends OnboardingState {
    loading: boolean;
    assignment: AthletePaymentAssignment | null;
    assignmentId: number | null;
    /** Pending coach/guardian invite (not yet accepted). */
    hasPendingSupportInvite: boolean;
    /** Pending guardian invite for coach onboarding (not yet accepted). */
    hasPendingGuardianInvite: boolean;
    /** Re-fetch assignment and recompute the checklist. */
    refresh: () => Promise<void>;
    returnSearch: string;
    withReturn: (path: string) => string;
    consumeReturn: () => string | null;
    clearReturn: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function useOnboarding() {
    const ctx = useContext(OnboardingContext);
    if (!ctx) {
        throw new Error("useOnboarding must be used within OnboardingProvider");
    }
    return ctx;
}

interface Props {
    children: ReactNode;
}

export function OnboardingProvider({ children }: Props) {
    const { isAuthenticated, user } = useAuthInfo();
    const { organization } = useAppContext();
    const { applyRoleLabels } = useRoleLabels();
    const location = useLocation();
    const { assignment_id: assignmentIdParam } = useParams<{ assignment_id?: string }>();

    const assignmentId = useMemo(() => {
        if (!assignmentIdParam) return null;
        const parsed = parseInt(assignmentIdParam, 10);
        return Number.isNaN(parsed) ? null : parsed;
    }, [assignmentIdParam]);

    const [assignment, setAssignment] = useState<AthletePaymentAssignment | null>(null);
    const [loading, setLoading] = useState(true);
    const [hasPendingSupportInvite, setHasPendingSupportInvite] = useState(false);
    const [hasPendingGuardianInvite, setHasPendingGuardianInvite] = useState(false);

    const [returnSearch, setReturnSearch] = useState<string>(() => {
        const fromUrl = getReturnSearch(typeof window !== "undefined" ? window.location.search : "");
        return fromUrl || loadStoredReturnSearch();
    });

    useEffect(() => {
        const fromUrl = getReturnSearch(location.search);
        if (fromUrl) {
            storeReturnParams(location.search);
            setReturnSearch(fromUrl);
        }
    }, [location.search]);

    const fetchPendingInvites = useCallback(async (id: number, mode: "support" | "guardian") => {
        try {
            const res = await ApiClient.get<AssignmentInvitation[]>(
                `/api/payment-assignments/${id}/invitations/`,
            );
            if (res.success && Array.isArray(res.data)) {
                if (mode === "support") {
                    const pending = res.data.some(
                        (inv) =>
                            inv.action === "pending" &&
                            (inv.role === "coach" || inv.role === "parent"),
                    );
                    setHasPendingSupportInvite(pending);
                } else {
                    const pending = res.data.some(
                        (inv) => inv.action === "pending" && inv.role === "parent",
                    );
                    setHasPendingGuardianInvite(pending);
                }
            } else if (mode === "support") {
                setHasPendingSupportInvite(false);
            } else {
                setHasPendingGuardianInvite(false);
            }
        } catch {
            if (mode === "support") {
                setHasPendingSupportInvite(false);
            } else {
                setHasPendingGuardianInvite(false);
            }
        }
    }, []);

    const fetchAssignment = useCallback(async () => {
        if (!isAuthenticated || !assignmentId) {
            setAssignment(null);
            setHasPendingSupportInvite(false);
            setHasPendingGuardianInvite(false);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const response: HttpResponse<AthletePaymentAssignment> = await ApiClient.get(
                `/api/athlete-assignments-by-id/${assignmentId}/`,
            );
            if (response.success && response.data) {
                setAssignment(response.data);
                const role = getParticipationRole(response.data.my_roles);
                if (
                    role === "athlete" &&
                    !assignmentHasCoachingSupport(response.data)
                ) {
                    await fetchPendingInvites(assignmentId, "support");
                    setHasPendingGuardianInvite(false);
                } else if (role === "coach") {
                    setHasPendingSupportInvite(false);
                    await fetchPendingInvites(assignmentId, "guardian");
                } else {
                    setHasPendingSupportInvite(false);
                    setHasPendingGuardianInvite(false);
                }
            } else {
                setAssignment(null);
                setHasPendingSupportInvite(false);
                setHasPendingGuardianInvite(false);
            }
        } catch (err) {
            console.error("[ONBOARDING] Failed to load assignment", err);
            setAssignment(null);
            setHasPendingSupportInvite(false);
            setHasPendingGuardianInvite(false);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, assignmentId, fetchPendingInvites]);

    useEffect(() => {
        fetchAssignment();
    }, [fetchAssignment]);

    useEffect(() => {
        const handler = () => fetchAssignment();
        window.addEventListener("smsp:waiver-accepted", handler);
        return () => window.removeEventListener("smsp:waiver-accepted", handler);
    }, [fetchAssignment]);

    const state = useMemo(
        () =>
            computeOnboardingSteps({
                user,
                assignment,
                assignmentId,
                orgWaiver: organization
                    ? {
                          waiver_athlete: organization.waiver_athlete ?? organization.waiver_name,
                          waiver_adult: organization.waiver_adult,
                      }
                    : null,
                applyRoleLabels,
                hasPendingSupportInvite,
                hasPendingGuardianInvite,
            }),
        [user, assignment, assignmentId, organization, applyRoleLabels, hasPendingSupportInvite, hasPendingGuardianInvite],
    );

    const withReturn = useCallback((path: string) => withReturnUtil(path, returnSearch), [returnSearch]);
    const consumeReturn = useCallback(() => consumeReturnTarget(returnSearch), [returnSearch]);
    const clearReturn = useCallback(() => {
        clearStoredReturnParams();
        setReturnSearch("");
    }, []);

    const value: OnboardingContextType = {
        ...state,
        loading,
        assignment,
        assignmentId,
        hasPendingSupportInvite,
        hasPendingGuardianInvite,
        refresh: fetchAssignment,
        returnSearch,
        withReturn,
        consumeReturn,
        clearReturn,
    };

    return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}
